package br.com.htms.inspeccaocampo.export

import br.com.htms.inspeccaocampo.data.os.OrdemServicoDados
import br.com.htms.inspeccaocampo.data.os.OsBlocoRisco
import br.com.htms.inspeccaocampo.data.os.OsCategoria
import br.com.htms.inspeccaocampo.platform.PlatformServices
import br.com.htms.inspeccaocampo.util.ImageHeaders
import kotlin.math.max

/**
 * PDF da Ordem de Serviço (NR-01 1.4.1 "c") no layout do modelo "OS - Montador":
 * A4 paisagem, logo HTMS no topo, tabela de riscos por categoria e termo de recebimento.
 */
object OrdemServicoPdfBuilder {
    private const val W = 842.0
    private const val H = 595.0
    private const val X0 = 34.0
    private const val X1 = W - 34.0
    private const val TOP = H - 86.0
    private const val BOTTOM = 62.0
    private const val FS = 8.0
    private const val LH = 10.2
    private const val PAD = 4.0
    private const val LOGO_ASSET = "os_logo_htms.jpg"

    private val PROVIDENCIAS = listOf(
        "Comunicar de imediato ao setor de QSMS sobre o acontecido",
        "Encaminhar ao hospital mais próximo o mais rápido possível, mediante acompanhamento da Segurança do Trabalho.",
        "Providenciar toda documentação do colaborador e acompanhar o processo de recuperação",
        "Dar ciência a todos os colaboradores da empresa do acontecido fazendo nova orientação e conscientização",
        "Abertura de CAT"
    )

    /** (texto, negrito) — NR-01 1.4.1 / 1.4.2 como no modelo. */
    private val TERMO = listOf(
        "1.4.1 Cabe ao empregador:" to true,
        "a) cumprir e fazer cumprir as disposições legais e regulamentares sobre segurança e saúde no trabalho;" to false,
        "b) informar aos trabalhadores:" to false,
        "I. os riscos ocupacionais existentes nos locais de trabalho;" to false,
        "II. as medidas de prevenção adotadas pela empresa para eliminar ou reduzir tais riscos;" to false,
        "III. os resultados dos exames médicos e de exames complementares de diagnóstico aos quais os próprios trabalhadores forem submetidos;" to false,
        "IV. os resultados das avaliações ambientais realizadas nos locais de trabalho." to false,
        "c) elaborar ordens de serviço sobre segurança e saúde no trabalho, dando ciência aos trabalhadores;" to false,
        "d) permitir que representantes dos trabalhadores acompanhem a fiscalização dos preceitos legais e regulamentares sobre segurança e saúde no trabalho;" to false,
        "e) determinar procedimentos que devem ser adotados em caso de acidente ou doença relacionada ao trabalho, incluindo a análise de suas causas;" to false,
        "f) disponibilizar à Inspeção do Trabalho todas as informações relativas à segurança e saúde no trabalho;" to false,
        "g) implementar medidas de prevenção, ouvidos os trabalhadores, de acordo com a seguinte ordem de prioridade:" to false,
        "I. eliminação dos fatores de risco;" to false,
        "II. minimização e controle dos fatores de risco, com a adoção de medidas de proteção coletiva;" to false,
        "III. minimização e controle dos fatores de risco, com a adoção de medidas administrativas ou de organização do trabalho;" to false,
        "IV. adoção de medidas de proteção individual." to false,
        "1.4.2 Cabe ao trabalhador:" to true,
        "a) cumprir as disposições legais e regulamentares sobre segurança e saúde no trabalho, inclusive as ordens de serviço expedidas pelo empregador;" to false,
        "b) submeter-se aos exames médicos previstos nas NR;" to false,
        "c) colaborar com a organização na aplicação das NR;" to false,
        "d) usar o equipamento de proteção individual fornecido pelo empregador." to false
    )

    suspend fun build(d: OrdemServicoDados): ByteArray {
        val logo = PlatformServices.loadBundledAsset(LOGO_ASSET)
            ?.takeIf { it.size > 2 && it[0] == 0xFF.toByte() && it[1] == 0xD8.toByte() }
        val doc = Doc(logo)
        doc.newPage()

        doc.textCentered("ORDEM DE SERVIÇO SOBRE HIGIENE, SEGURANÇA E MEDICINA DO TRABALHO.", X0, X1, doc.y - 12, 11.0, true)
        doc.y -= 26
        val intro = "Conforme estabelecido no item 1.4, letra “b”, NR-01 da Portaria 3214/MTE, cabe ao empregador " +
            "elaborar Ordem de Serviço (OS) sobre Segurança e Medicina do Trabalho, dando ciência aos empregados."
        wrap(intro, X1 - X0 - 40, FS, false).forEach { linha ->
            doc.textCentered(linha, X0, X1, doc.y - FS, FS, false)
            doc.y -= LH
        }
        doc.y -= 8
        identificacao(doc, d)
        doc.y -= 12
        tabelaRiscos(doc, d.blocos)

        doc.newPage()
        caixaTitulo(doc, "DESCRIÇÃO DA FUNÇÃO /ATIVIDADES HABITUAIS",
            listOf(d.descricaoFuncao.ifBlank { "-" } to false), bullet = false, minBodyH = 70.0)
        doc.y -= 14
        caixaTitulo(doc, "TECNOLOGIA DE PROTEÇÃO RECOMENDADA EPI/EPC",
            d.epis.ifEmpty { listOf("-") }.map { it to false }, bullet = true)
        doc.y -= 14
        caixaTitulo(doc, "PROVIDÊNCIAS MÍNIMAS A SEREM TOMADAS EM CASO DE ACIDENTES OU INCIDENTES",
            PROVIDENCIAS.map { it to false }, bullet = true, minBodyH = 80.0)

        doc.newPage()
        termo(doc)

        doc.newPage()
        recibo(doc, d)

        return doc.finish()
    }

    private fun identificacao(doc: Doc, d: OrdemServicoDados) {
        val c1 = X0 + 58.0
        val c2 = X0 + 360.0
        val rowH = 18.0
        fun linha(rot: String, valor: String, direita: Pair<String, String>?) {
            val top = doc.y
            val bottom = top - rowH
            doc.rect(X0, bottom, X1 - X0, rowH)
            doc.vline(c1, bottom, top)
            doc.text(rot, X0 + PAD, bottom + 6, FS, true)
            doc.text(truncar(valor, (if (direita != null) c2 else X1) - c1 - PAD * 2, FS, false), c1 + PAD, bottom + 6, FS, false)
            if (direita != null) {
                doc.vline(c2, bottom, top)
                val rw = widthOf(direita.first + " ", FS, true)
                doc.text(direita.first, c2 + PAD, bottom + 6, FS, true)
                doc.text(truncar(direita.second, X1 - c2 - rw - PAD * 2, FS, false), c2 + PAD + rw, bottom + 6, FS, false)
            }
            doc.y = bottom
        }
        linha("NOME", d.nome.uppercase(), null)
        linha("FUNÇÃO", d.funcao, "SETOR/GHE:" to d.setorGhe)
        linha("EMPRESA", d.empresa, "ADMISSÃO:" to d.admissao)
    }

    private fun tabelaRiscos(doc: Doc, blocos: List<OsBlocoRisco>) {
        val xCat = X0
        val xRisco = X0 + 92.0
        val xDano = xRisco + 160.0
        val xMed = xDano + 232.0
        val wRisco = xDano - xRisco - PAD * 2
        val wDano = xMed - xDano - PAD * 2
        val wMed = X1 - xMed - PAD * 2

        fun cabecalho() {
            val h1 = 18.0
            doc.rect(X0, doc.y - h1, X1 - X0, h1)
            doc.textCentered("RISCOS DE ACIDENTES NO CASO DO NÃO CUMPRIMENTO DO PROCEDIMENTO", X0, X1, doc.y - 13, 10.5, true)
            doc.y -= h1
            val h2 = 15.0
            val b = doc.y - h2
            doc.rect(X0, b, X1 - X0, h2)
            doc.vline(xDano, b, doc.y)
            doc.vline(xMed, b, doc.y)
            doc.textCentered("RISCOS OCUPACIONAIS", xCat, xDano, b + 5, FS + 0.5, true)
            doc.textCentered("POSSÍVEIS DANOS A SAÚDE", xDano, xMed, b + 5, FS + 0.5, true)
            doc.textCentered("MEDIDAS DE CONTROLE", xMed, X1, b + 5, FS + 0.5, true)
            doc.y = b
        }

        if (doc.y - 60 < BOTTOM) doc.newPage()
        cabecalho()

        blocos.forEach { bloco ->
            data class Item(val r: List<String>, val d: List<String>, val m: List<String>) {
                val linhas get() = max(r.size, max(d.size, m.size))
            }
            val itens = bloco.itens.map {
                Item(
                    wrap("${it.numero}. ${it.risco}", wRisco, FS, false),
                    wrap("${it.numero}. ${it.dano}", wDano, FS, false),
                    wrap("${it.numero}. ${it.medida}", wMed, FS, false)
                )
            }
            var i = 0
            var primeiraParte = true
            while (i < itens.size) {
                val disponivel = doc.y - BOTTOM - PAD * 2
                var usadas = 0.0
                val parte = mutableListOf<Item>()
                while (i < itens.size) {
                    val h = itens[i].linhas * LH + 3.0
                    if (parte.isNotEmpty() && usadas + h > disponivel) break
                    if (parte.isEmpty() && h > disponivel && doc.y < TOP - 1) break
                    parte += itens[i]
                    usadas += h
                    i++
                }
                if (parte.isEmpty()) {
                    doc.newPage()
                    continue
                }
                val rowH = max(usadas + PAD * 2, 24.0)
                val bottom = doc.y - rowH
                doc.fill(xCat, bottom, xRisco - xCat, rowH, bloco.categoria.corRgb)
                doc.rect(X0, bottom, X1 - X0, rowH)
                doc.vline(xRisco, bottom, doc.y)
                doc.vline(xDano, bottom, doc.y)
                doc.vline(xMed, bottom, doc.y)
                if (primeiraParte) {
                    val corTexto = if (bloco.categoria == OsCategoria.QUIMICOS || bloco.categoria == OsCategoria.BIOLOGICO) 0xFFFFFF else 0x000000
                    doc.textCentered(bloco.categoria.rotulo, xCat, xRisco, bottom + rowH / 2 - 3, FS, true, corTexto)
                }
                var yLinha = doc.y - PAD - FS
                parte.forEach { it ->
                    it.r.forEachIndexed { k, s -> doc.text(s, xRisco + PAD, yLinha - k * LH, FS, false) }
                    it.d.forEachIndexed { k, s -> doc.text(s, xDano + PAD, yLinha - k * LH, FS, false) }
                    it.m.forEachIndexed { k, s -> doc.text(s, xMed + PAD, yLinha - k * LH, FS, false) }
                    yLinha -= it.linhas * LH + 3.0
                }
                doc.y = bottom
                primeiraParte = false
                if (i < itens.size) doc.newPage()
            }
        }
    }

    private fun caixaTitulo(
        doc: Doc,
        titulo: String,
        linhas: List<Pair<String, Boolean>>,
        bullet: Boolean,
        minBodyH: Double = 0.0
    ) {
        val bx0 = X0 + 20
        val bx1 = X1 - 20
        val indent = if (bullet) 26.0 else PAD
        val corpo = linhas.flatMap { (t, b) ->
            wrap(t, bx1 - bx0 - indent - PAD, FS, b).mapIndexed { k, s -> Triple(s, b, k == 0) }
        }
        val titH = 15.0
        val bodyH = max(corpo.size * LH + PAD * 2 + 2, minBodyH)
        if (doc.y - titH - bodyH < BOTTOM) doc.newPage()
        doc.rect(bx0, doc.y - titH, bx1 - bx0, titH)
        doc.textCentered(titulo, bx0, bx1, doc.y - titH + 5, FS + 0.5, true)
        doc.y -= titH
        val bottom = doc.y - bodyH
        doc.rect(bx0, bottom, bx1 - bx0, bodyH)
        var yl = doc.y - PAD - FS
        corpo.forEach { (s, b, primeira) ->
            if (bullet && primeira) doc.text("–", bx0 + 10, yl, FS, false)
            doc.text(s, bx0 + indent, yl, FS, b)
            yl -= LH
        }
        doc.y = bottom
    }

    private fun termo(doc: Doc) {
        val bx0 = X0 + 20
        val bx1 = X1 - 20
        val corpo = TERMO.flatMap { (t, b) -> wrap(t, bx1 - bx0 - PAD * 2, FS + 0.5, b).map { it to b } }
        val h = 16.0 + 14.0 + 8.0 + corpo.size * (LH + 0.6) + PAD * 2
        val top = doc.y
        doc.rect(bx0, top - h, bx1 - bx0, h)
        doc.textCentered("TERMO DE RECEBIMENTO E COMPROMISSO", bx0, bx1, top - 13, 10.5, true)
        doc.hline(bx0, bx1, top - 17)
        doc.textCentered("Portaria 3214 de julho de 1978 – Norma Regulamentadora NR-1 DISPOSIÇÕES GERAIS", bx0, bx1, top - 28, FS, false)
        var yl = top - 46
        corpo.forEach { (s, b) ->
            doc.text(s, bx0 + PAD, yl, FS + 0.5, b)
            yl -= LH + 0.6
        }
        doc.y = top - h
    }

    private fun recibo(doc: Doc, d: OrdemServicoDados) {
        val bx0 = X0 + 20
        val bx1 = X1 - 20
        val texto = "Recebi da Empresa HTMS ENERGIA a ordem de serviço de mesmo teor desta que assino, referente às " +
            "minhas funções, que foi elaborado considerando a legislação trabalhista em vigor, a qual cumprirei. " +
            "Tomo ciência também, que o não cumprimento de qualquer item desta O.S. implica em punição de acordo " +
            "com a legislação trabalhista e as normas da empresa."
        val h = 360.0
        val top = doc.y
        doc.rect(bx0, top - h, bx1 - bx0, h)
        var yl = top - PAD - FS - 2
        wrap(texto, bx1 - bx0 - PAD * 2, FS + 0.5, false).forEach {
            doc.text(it, bx0 + PAD, yl, FS + 0.5, false)
            yl -= LH + 1
        }
        doc.text("Assinatura do Funcionário:", bx0 + PAD, top - 190, 9.0, true)
        doc.text("Assinatura do departamento de QSMS:", bx0 + PAD, top - 262, 9.0, true)
        doc.textCentered(d.localData, bx0, bx1, top - 330, 9.5, true)
        doc.y = top - h
    }

    // ---------------------------------------------------------------- layout

    private class Doc(private val logo: ByteArray?) {
        private val pages = mutableListOf<String>()
        private val ops = StringBuilder()
        var y = TOP
        private val logoSize = logo?.let { ImageHeaders.size(it) }

        fun newPage() {
            if (ops.isNotEmpty()) pages += ops.toString()
            ops.clear()
            y = TOP
            moldura(pages.size + 1)
        }

        private fun moldura(numero: Int) {
            if (logo != null && logoSize != null) {
                val h = 40.0
                val w = h * logoSize.width / logoSize.height.toDouble()
                ops.append("q\n$w 0 0 $h 16 ${H - 16 - h} cm\n/Im1 Do\nQ\n")
            }
            val fy = 44.0
            ops.append("q\n0.45 0.1 0.1 RG\n1.6 w\n${X0 + 36} $fy m\n${X1 - 36} $fy l\nS\n")
            ops.append("0.6 w\n${X0 + 36} ${fy - 2.4} m\n${X1 - 36} ${fy - 2.4} l\nS\nQ\n")
            text("1VIA. EMPREGADOR / 2VIA. EMPREGADO", X0 + 40, fy - 16, 9.0, false)
            text("Página $numero", W / 2 + 40, fy - 16, 9.0, false)
        }

        fun rect(x: Double, bottom: Double, w: Double, h: Double) {
            ops.append("q\n0.6 w\n0.35 0.35 0.35 RG\n${f(x)} ${f(bottom)} ${f(w)} ${f(h)} re\nS\nQ\n")
        }

        fun fill(x: Double, bottom: Double, w: Double, h: Double, rgb: Int) {
            ops.append("q\n${rgbOp(rgb)} rg\n${f(x)} ${f(bottom)} ${f(w)} ${f(h)} re\nf\nQ\n")
        }

        fun vline(x: Double, y1: Double, y2: Double) {
            ops.append("q\n0.6 w\n0.35 0.35 0.35 RG\n${f(x)} ${f(y1)} m\n${f(x)} ${f(y2)} l\nS\nQ\n")
        }

        fun hline(x1: Double, x2: Double, yy: Double) {
            ops.append("q\n0.6 w\n0.35 0.35 0.35 RG\n${f(x1)} ${f(yy)} m\n${f(x2)} ${f(yy)} l\nS\nQ\n")
        }

        fun text(s: String, x: Double, baseline: Double, size: Double, bold: Boolean, rgb: Int = 0) {
            if (s.isEmpty()) return
            val font = if (bold) "/F2" else "/F1"
            ops.append("q\n${rgbOp(rgb)} rg\nBT\n$font ${f(size)} Tf\n${f(x)} ${f(baseline)} Td\n(${escape(s)}) Tj\nET\nQ\n")
        }

        fun textCentered(s: String, xa: Double, xb: Double, baseline: Double, size: Double, bold: Boolean, rgb: Int = 0) {
            val w = widthOf(s, size, bold)
            text(s, xa + max(0.0, (xb - xa - w) / 2), baseline, size, bold, rgb)
        }

        fun finish(): ByteArray {
            if (ops.isNotEmpty()) pages += ops.toString()
            return assemble(pages, logo)
        }
    }

    private fun f(v: Double): String {
        val r = kotlin.math.round(v * 100) / 100.0
        return if (r == r.toLong().toDouble()) r.toLong().toString() else r.toString()
    }

    private fun rgbOp(rgb: Int): String {
        val r = ((rgb shr 16) and 0xFF) / 255.0
        val g = ((rgb shr 8) and 0xFF) / 255.0
        val b = (rgb and 0xFF) / 255.0
        return "${f(r)} ${f(g)} ${f(b)}"
    }

    private fun truncar(s: String, maxW: Double, size: Double, bold: Boolean): String {
        if (widthOf(s, size, bold) <= maxW) return s
        var fim = s.length
        while (fim > 0 && widthOf(s.take(fim) + "...", size, bold) > maxW) fim--
        return s.take(fim) + "..."
    }

    private fun wrap(text: String, maxW: Double, size: Double, bold: Boolean): List<String> {
        val out = mutableListOf<String>()
        text.replace('\r', ' ').split('\n').forEach { par ->
            var atual = ""
            par.split(Regex("\\s+")).filter { it.isNotEmpty() }.forEach { palavra ->
                val cand = if (atual.isEmpty()) palavra else "$atual $palavra"
                if (widthOf(cand, size, bold) <= maxW) {
                    atual = cand
                } else {
                    if (atual.isNotEmpty()) out += atual
                    atual = if (widthOf(palavra, size, bold) <= maxW) palavra else truncar(palavra, maxW, size, bold)
                }
            }
            if (atual.isNotEmpty()) out += atual
        }
        return out.ifEmpty { listOf("") }
    }

    // Larguras Helvetica / Helvetica-Bold (AFM, 1/1000 em) para ASCII 32..126.
    private val HELV = intArrayOf(
        278, 278, 355, 556, 556, 889, 667, 191, 333, 333, 389, 584, 278, 333, 278, 278,
        556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 278, 278, 584, 584, 584, 556,
        1015, 667, 667, 722, 722, 667, 611, 778, 722, 278, 500, 667, 556, 833, 722, 778,
        667, 778, 722, 667, 611, 722, 667, 944, 667, 667, 611, 278, 278, 278, 469, 556,
        333, 556, 556, 500, 556, 556, 278, 556, 556, 222, 222, 500, 222, 833, 556, 556,
        556, 556, 333, 500, 278, 556, 500, 722, 500, 500, 500, 334, 260, 334, 584
    )
    private val HELV_B = intArrayOf(
        278, 333, 474, 556, 556, 889, 722, 238, 333, 333, 389, 584, 278, 333, 278, 278,
        556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 333, 333, 584, 584, 584, 611,
        975, 722, 722, 722, 722, 667, 611, 778, 722, 278, 556, 722, 611, 833, 722, 778,
        667, 778, 722, 667, 611, 722, 667, 944, 667, 667, 611, 333, 278, 333, 584, 556,
        333, 556, 611, 556, 611, 556, 333, 611, 611, 278, 278, 556, 278, 889, 611, 611,
        611, 611, 389, 556, 333, 611, 556, 778, 556, 556, 500, 389, 280, 389, 584
    )

    private fun baseChar(ch: Char): Char = when (ch) {
        'á', 'à', 'â', 'ã', 'ä' -> 'a'
        'Á', 'À', 'Â', 'Ã', 'Ä' -> 'A'
        'é', 'è', 'ê', 'ë' -> 'e'
        'É', 'È', 'Ê', 'Ë' -> 'E'
        'í', 'ì', 'î', 'ï' -> 'i'
        'Í', 'Ì', 'Î', 'Ï' -> 'I'
        'ó', 'ò', 'ô', 'õ', 'ö' -> 'o'
        'Ó', 'Ò', 'Ô', 'Õ', 'Ö' -> 'O'
        'ú', 'ù', 'û', 'ü' -> 'u'
        'Ú', 'Ù', 'Û', 'Ü' -> 'U'
        'ç' -> 'c'
        'Ç' -> 'C'
        'ñ' -> 'n'
        'Ñ' -> 'N'
        '“', '”' -> '"'
        '‘', '’', '´' -> '\''
        '–' -> '-'
        else -> ch
    }

    private fun widthOf(s: String, size: Double, bold: Boolean): Double {
        val tab = if (bold) HELV_B else HELV
        var w = 0
        for (ch in s) {
            val c = baseChar(ch).code
            w += when {
                c in 32..126 -> tab[c - 32]
                ch == '—' -> 1000
                else -> 556
            }
        }
        return w * size / 1000.0
    }

    private fun toWinAnsi(ch: Char): Int {
        val code = ch.code
        if (code in 32..126 || code in 160..255) return code
        return when (ch) {
            '–' -> 150
            '—' -> 151
            '“' -> 147
            '”' -> 148
            '‘' -> 145
            '’' -> 146
            '•' -> 149
            '…' -> 133
            else -> '?'.code
        }
    }

    private fun escape(s: String): String = buildString(s.length + 8) {
        for (ch in s) {
            val c = toWinAnsi(ch)
            when {
                c == '\\'.code -> append("\\\\")
                c == '('.code -> append("\\(")
                c == ')'.code -> append("\\)")
                c in 32..126 -> append(c.toChar())
                else -> append("\\").append(c.toString(8).padStart(3, '0'))
            }
        }
    }

    private fun latin1(s: String): ByteArray = ByteArray(s.length) { i ->
        val c = s[i].code
        (if (c in 0..255) c else '?'.code).toByte()
    }

    private fun assemble(pages: List<String>, logo: ByteArray?): ByteArray {
        val logoSize = logo?.let { ImageHeaders.size(it) }
        val temLogo = logo != null && logoSize != null
        val objs = linkedMapOf<Int, ByteArray>()
        val firstPage = if (temLogo) 6 else 5
        val kids = pages.indices.joinToString(" ") { "${firstPage + it * 2} 0 R" }
        objs[1] = latin1("<< /Type /Catalog /Pages 2 0 R >>")
        objs[2] = latin1("<< /Type /Pages /Kids [ $kids ] /Count ${pages.size} >>")
        objs[3] = latin1("<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>")
        objs[4] = latin1("<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>")
        if (temLogo) {
            objs[5] = latin1(
                "<< /Type /XObject /Subtype /Image /Width ${logoSize!!.width} /Height ${logoSize.height} " +
                    "/ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${logo.size} >>\nstream\n"
            ) + logo + latin1("\nendstream")
        }
        val xobj = if (temLogo) "/XObject << /Im1 5 0 R >> " else ""
        pages.forEachIndexed { i, content ->
            val p = firstPage + i * 2
            objs[p] = latin1(
                "<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${W.toInt()} ${H.toInt()}] /Contents ${p + 1} 0 R " +
                    "/Resources << /Font << /F1 3 0 R /F2 4 0 R >> $xobj>> >>"
            )
            val bytes = latin1(content)
            objs[p + 1] = latin1("<< /Length ${bytes.size} >>\nstream\n") + bytes + latin1("\nendstream")
        }
        val info = firstPage + pages.size * 2
        objs[info] = latin1("<< /Producer (ICD-OrdemServicoPdfBuilder) /Title (Ordem de Servico) >>")

        val out = ArrayList<Byte>(64 * 1024)
        fun add(b: ByteArray) { b.forEach { out.add(it) } }
        add(latin1("%PDF-1.4\n%\u00e2\u00e3\u00cf\u00d3\n"))
        val offsets = IntArray(info + 1)
        for (n in 1..info) {
            offsets[n] = out.size
            add(latin1("$n 0 obj\n"))
            add(objs[n] ?: latin1("<< >>"))
            add(latin1("\nendobj\n"))
        }
        val xref = out.size
        val sb = StringBuilder("xref\n0 ${info + 1}\n0000000000 65535 f \n")
        for (n in 1..info) sb.append(offsets[n].toString().padStart(10, '0')).append(" 00000 n \n")
        sb.append("trailer\n<< /Size ${info + 1} /Root 1 0 R /Info $info 0 R >>\nstartxref\n$xref\n%%EOF")
        add(latin1(sb.toString()))
        return out.toByteArray()
    }
}
